import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, FileEdit, X, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { timeAgo } from "@/lib/format";
import { READINESS, readinessOf, readinessMeta, readinessRank, draftPolicy } from "@/lib/readiness";

const PRIORITY_COLOR = {
  "Breaking": "text-crimson border-crimson", "High Priority": "text-amber border-amber",
  "Normal": "text-cyan border-cyan", "Low Priority": "text-admmuted border-admborder",
};
const STATUS_BADGE = {
  new: "bg-cyan/15 text-cyan", researched: "bg-amber/15 text-amber",
  drafted: "bg-emerald/15 text-emerald", ignored: "bg-admmuted/15 text-admmuted",
  suppressed: "bg-crimson/15 text-crimson",
};
const TABS = [["all", "All"], ["new", "New"], ["researched", "Researched"], ["drafted", "Drafted"], ["suppressed", "Suppressed"], ["ignored", "Ignored"]];
const SORTS = [["readiness", "Editorial Readiness"], ["importance", "Importance"], ["newest", "Newest"], ["oldest", "Oldest"], ["most_sources", "Most Sources"], ["least_verified", "Least Verified"]];

const rq = (c) => c.research || {};
const nSources = (c) => rq(c).total_discovered ?? (c.sources || []).length;
const nPrimary = (c) => (rq(c).primary_sources || []).length;
const nRead = (c) => rq(c).sources_read ?? 0;
const nUnverified = (c) => (rq(c).unverified_claims || rq(c).caution || []).length;

export default function Candidates() {
  const [all, setAll] = useState([]);
  const [tab, setTab] = useState("new");
  const [readiness, setReadiness] = useState("all");
  const [sort, setSort] = useState("readiness");
  const [busy, setBusy] = useState({});
  const nav = useNavigate();

  const load = useCallback(() => {
    api.get("/agent/candidates").then((r) => setAll(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const counts = all.reduce((acc, c) => { acc[c.status] = (acc[c.status] || 0) + 1; return acc; }, {});
  const showReadiness = tab === "researched" || tab === "all";
  const researchedPool = all.filter((c) => readinessOf(c));
  const readinessCounts = useMemo(() => {
    const m = { all: researchedPool.length };
    READINESS.forEach((r) => { m[r.key] = researchedPool.filter((c) => readinessOf(c) === r.key).length; });
    return m;
  }, [researchedPool]);

  const items = useMemo(() => {
    let list = tab === "all" ? [...all] : all.filter((c) => c.status === tab);
    if (showReadiness && readiness !== "all") list = list.filter((c) => readinessOf(c) === readiness);
    const cmp = {
      readiness: (a, b) => (readinessRank(a) - readinessRank(b)) || (b.importance_score - a.importance_score),
      importance: (a, b) => b.importance_score - a.importance_score,
      newest: (a, b) => (b.discovered_at || "").localeCompare(a.discovered_at || ""),
      oldest: (a, b) => (a.discovered_at || "").localeCompare(b.discovered_at || ""),
      most_sources: (a, b) => nSources(b) - nSources(a),
      least_verified: (a, b) => nUnverified(b) - nUnverified(a),
    }[sort];
    return [...list].sort(cmp);
  }, [all, tab, readiness, sort, showReadiness]);

  const act = async (id, action, e, c) => {
    e.stopPropagation();
    if (action === "draft") {
      const pol = draftPolicy(readinessOf(c), nUnverified(c));
      if (pol.mode === "blocked") { toast.error("‘DO NOT PUBLISH’ — draft disabled. Open the story to override."); return; }
      if ((pol.mode === "warn" || pol.mode === "confirm") && !window.confirm(pol.msg)) return;
    }
    setBusy((b) => ({ ...b, [id]: action }));
    try {
      if (action === "research") { nav(`/admin/candidates/${id}?research=1`); return; }
      if (action === "draft") { await api.post(`/agent/candidates/${id}/draft`); toast.success("Draft creation started — see Articles → Awaiting Review shortly."); }
      if (action === "ignore") { await api.post(`/agent/candidates/${id}/ignore`); toast.success("Moved to Ignored."); }
      if (action === "promote") { await api.post(`/agent/candidates/${id}/promote`); toast.success("Promoted to Candidate."); }
    } catch (err) { toast.error("Action failed"); }
    finally { setTimeout(() => { setBusy((b) => ({ ...b, [id]: null })); load(); }, 1000); }
  };

  return (
    <AdminLayout title="Candidate Stories">
      <p className="text-sm text-admtext2 mb-4">Developments discovered by THE FIN DESK EDITOR. Research further, create a draft, or ignore. Nothing is published automatically.</p>

      {/* Workflow status tabs */}
      <div className="flex gap-1 flex-wrap mb-3" data-testid="candidate-tabs">
        {TABS.map(([val, label]) => (
          <button key={val} onClick={() => setTab(val)} data-testid={`candidate-tab-${val}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-wider border flex items-center gap-2 ${tab === val ? "border-cyan text-cyan bg-cyan/5" : "border-admborder text-admtext2 hover:text-admtext"}`}>
            {label}
            <span className="font-mono text-[11px] bg-admbg px-1.5 rounded-sm" data-testid={`candidate-count-${val}`}>
              {val === "all" ? all.length : (counts[val] || 0)}
            </span>
          </button>
        ))}
      </div>

      {/* Editorial readiness filter (shown for researched/all) */}
      {showReadiness && (
        <div className="flex flex-wrap items-center gap-1 mb-3" data-testid="readiness-filters">
          <span className="text-[10px] uppercase tracking-widest text-admmuted mr-1">Editorial Readiness</span>
          <button onClick={() => setReadiness("all")} data-testid="readiness-all"
            className={`px-2.5 py-1 text-[11px] uppercase tracking-wider border flex items-center gap-1.5 ${readiness === "all" ? "border-cyan text-cyan bg-cyan/5" : "border-admborder text-admtext2 hover:text-admtext"}`}>
            All <span className="font-mono bg-admbg px-1 rounded-sm">{readinessCounts.all}</span>
          </button>
          {READINESS.map((r) => (
            <button key={r.key} onClick={() => setReadiness(r.key)} data-testid={`readiness-${r.short || r.key}`}
              className={`px-2.5 py-1 text-[11px] uppercase tracking-wider border flex items-center gap-1.5 ${readiness === r.key ? r.color : "border-admborder text-admtext2 hover:text-admtext"}`}>
              {r.short || r.label} <span className="font-mono bg-admbg px-1 rounded-sm">{readinessCounts[r.key] || 0}</span>
            </button>
          ))}
        </div>
      )}

      {/* Sorting */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[10px] uppercase tracking-widest text-admmuted">Sort</span>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="candidate-sort"
          className="bg-admbg border border-admborder text-admtext2 text-xs px-2 py-1 outline-none focus:border-cyan">
          {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <span className="text-[10px] text-admmuted font-mono ml-auto">{items.length} shown</span>
      </div>

      <div className="space-y-3">
        {items.map((c) => {
          const action = readinessOf(c);
          const meta = readinessMeta(action);
          const pol = draftPolicy(action, nUnverified(c));
          const researched = !!action || c.status === "researched";
          return (
          <div key={c.id} onClick={() => nav(`/admin/candidates/${c.id}`)} data-testid={`candidate-${c.id}`}
            className="bg-admcard border border-admborder p-5 cursor-pointer hover:border-cyan transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${PRIORITY_COLOR[c.priority] || ""}`}>{c.priority}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${STATUS_BADGE[c.status] || ""}`} data-testid={`candidate-status-${c.id}`}>{c.status}</span>
                  {meta && <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${meta.color}`} data-testid={`candidate-readiness-${c.id}`}>{meta.label}</span>}
                  <span className="text-[10px] uppercase tracking-wider text-admmuted">{c.category}</span>
                  <span className="text-[10px] text-admmuted font-mono">· {timeAgo(c.discovered_at)}</span>
                </div>
                <h3 className="font-serif text-xl text-admtext leading-snug">{c.headline}</h3>
                <p className="text-sm text-admtext2 mt-1.5 leading-relaxed line-clamp-2">{c.summary}</p>
                {c.status === "suppressed" && c.suppression_reason && (
                  <p className="text-[11px] text-crimson mt-1.5 uppercase tracking-wider" data-testid={`candidate-suppressed-reason-${c.id}`}>Suppressed — {c.suppression_reason}</p>
                )}
                {researched && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] font-mono text-admmuted" data-testid={`candidate-quality-${c.id}`}>
                    <span>Sources: <span className="text-admtext">{nSources(c)}</span></span>
                    <span>Primary: <span className="text-admtext">{nPrimary(c)}</span></span>
                    <span>Read: <span className="text-emerald">{nRead(c)}</span></span>
                    <span>Unverified: <span className={nUnverified(c) ? "text-amber" : "text-admtext"}>{nUnverified(c)}</span></span>
                  </div>
                )}
              </div>
              <div className="text-center shrink-0 flex gap-4">
                <div>
                  <div className="font-mono text-3xl text-cyan">{c.importance_score}</div>
                  <div className="text-[10px] text-admmuted uppercase">Importance</div>
                </div>
                <div>
                  <div className={`font-mono text-3xl ${(c.relevance_score ?? 50) >= 80 ? "text-emerald" : (c.relevance_score ?? 50) >= 40 ? "text-amber" : "text-crimson"}`} data-testid={`candidate-relevance-${c.id}`}>{c.relevance_score ?? "—"}</div>
                  <div className="text-[10px] text-admmuted uppercase">Fin Desk Rel.</div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
              <button onClick={(e) => act(c.id, "research", e, c)} disabled={busy[c.id]} data-testid={`candidate-research-${c.id}`}
                className="flex items-center gap-1.5 border border-admborder text-admtext px-3 py-1.5 text-xs uppercase tracking-wider hover:border-cyan disabled:opacity-50"><Search size={13} /> Research Further</button>
              {pol.mode === "blocked" ? (
                <button onClick={(e) => { e.stopPropagation(); nav(`/admin/candidates/${c.id}`); }} data-testid={`candidate-draft-blocked-${c.id}`}
                  className="flex items-center gap-1.5 border border-crimson/50 text-crimson px-3 py-1.5 text-xs uppercase tracking-wider cursor-not-allowed"
                  title="Draft disabled — open story to override"><ShieldAlert size={13} /> Draft Disabled</button>
              ) : (
                <button onClick={(e) => act(c.id, "draft", e, c)} disabled={busy[c.id]} data-testid={`candidate-draft-${c.id}`}
                  className="flex items-center gap-1.5 bg-cyan text-admbg px-3 py-1.5 text-xs font-bold uppercase tracking-wider hover:opacity-90 disabled:opacity-50">
                  <FileEdit size={13} /> {busy[c.id] === "draft" ? "Drafting…" : "Create Draft"}</button>
              )}
              {c.status === "suppressed" && (
                <button onClick={(e) => act(c.id, "promote", e, c)} disabled={busy[c.id]} data-testid={`candidate-promote-${c.id}`}
                  className="flex items-center gap-1.5 border border-emerald/50 text-emerald px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-emerald/10 disabled:opacity-50"><FileEdit size={13} /> Promote to Candidate</button>
              )}
              <button onClick={(e) => act(c.id, "ignore", e, c)} disabled={busy[c.id]} data-testid={`candidate-ignore-${c.id}`}
                className="flex items-center gap-1.5 border border-admborder text-admmuted px-3 py-1.5 text-xs uppercase tracking-wider hover:border-crimson hover:text-crimson disabled:opacity-50"><X size={13} /> Ignore</button>
              <span className="ml-auto text-xs text-cyan">Open →</span>
            </div>
          </div>
        );})}
        {!items.length && <div className="bg-admcard border border-admborder p-10 text-center text-admmuted" data-testid="candidates-empty">No candidate stories in this view.</div>}
      </div>
    </AdminLayout>
  );
}
